// Reviiri import panel (CLUB-191). Two steps rather than the Oma Riista panel's one:
// Reviiri needs an account sign-in, and an account can see several club maps, so the
// user connects and then picks. The panel opens by TRYING the map list first — a user
// who connected on mobile is already signed in, and asking them for a password they
// do not need to type is the mistake worth avoiding.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { HudCard, HudHeader } from './HudCard';
import {
  canSubmitReviiriLogin,
  reviiriMapCount,
  type ReviiriClub,
  type ReviiriClubsResult,
  type ReviiriConnectResult,
  type ReviiriImportResult,
} from '../lib/reviiri';

type Stage =
  | 'loading'      // resolving whether an account is already connected
  | 'connect'      // sign-in form
  | 'connecting'
  | 'pick'         // choose a map
  | 'importing'
  | 'created'
  | 'refreshed'
  | 'load-error';  // the club list itself could not be read

/** Which message, if any, sits under the current step. */
type Notice =
  | { kind: 'none' }
  | { kind: 'rejected' }
  | { kind: 'not-found' }
  | { kind: 'error'; code: string | null };

export function ReviiriImportFlow({
  onConnect,
  onLoadClubs,
  onImport,
  onClose,
}: {
  onConnect: (email: string, password: string) => Promise<ReviiriConnectResult>;
  onLoadClubs: () => Promise<ReviiriClubsResult>;
  onImport: (mapId: string) => Promise<ReviiriImportResult>;
  onClose: () => void;
}) {
  const { t } = useTranslation('map');
  const [stage, setStage] = useState<Stage>('loading');
  const [notice, setNotice] = useState<Notice>({ kind: 'none' });
  const [clubs, setClubs] = useState<ReviiriClub[]>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedMapId, setSelectedMapId] = useState<string | null>(null);

  // Ignore results from a superseded request (panel closed, or the user retried).
  const requestToken = useRef(0);

  /** Drops the password from state the moment it is no longer needed. */
  const forgetPassword = () => setPassword('');

  // The host declares its handlers inline, so their identity changes on every parent
  // render — and an import's own refetch (awaited inside onImportReviiri before it
  // resolves) triggers one right after a fresh "created"/"refreshed" confirmation
  // appears. Re-running the initial load on that would reset the panel out of its own
  // success confirmation and silently clear the user's map selection, so the load runs
  // once per open (a mount-only effect below) and reads the handler through a ref.
  const onLoadClubsRef = useRef(onLoadClubs);
  useEffect(() => {
    onLoadClubsRef.current = onLoadClubs;
  });

  const loadClubs = useCallback(async () => {
    const token = ++requestToken.current;
    // Clear any prior notice: a successful retry must not leave the failed
    // attempt's error banner rendered underneath the recovered UI. NoticeText is
    // not gated on stage, so only clearing here keeps the two in step.
    setNotice({ kind: 'none' });
    setStage('loading');
    try {
      const result = await onLoadClubsRef.current();
      if (token !== requestToken.current) return;
      if (result.status === 'ok') {
        setClubs(result.clubs);
        setSelectedMapId(null);
        setStage('pick');
      } else if (result.status === 'not-connected') {
        setClubs([]);
        setStage('connect');
      } else {
        setNotice({ kind: 'error', code: result.code });
        setStage('load-error');
      }
    } catch {
      if (token !== requestToken.current) return;
      setNotice({ kind: 'error', code: null });
      setStage('load-error');
    }
    // Stable on purpose — see onLoadClubsRef above. A dep on onLoadClubs here is exactly
    // what re-triggers the mount effect below on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadClubs();
    // Mount-only: the panel resolves connection state exactly once per open, never again
    // just because the parent re-rendered. Retries go through the explicit buttons below,
    // which call the (stable) loadClubs directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canConnect = canSubmitReviiriLogin(email, password) && stage === 'connect';

  const connect = () => {
    if (!canConnect) return;
    const trimmed = email.trim();
    const token = ++requestToken.current;
    setNotice({ kind: 'none' });
    setStage('connecting');
    onConnect(trimmed, password)
      .then((result) => {
        if (token !== requestToken.current) return;
        if (result.status === 'connected') {
          forgetPassword();
          void loadClubs();
        } else if (result.status === 'rejected') {
          setNotice({ kind: 'rejected' });
          setStage('connect');
        } else {
          setNotice({ kind: 'error', code: result.code });
          setStage('connect');
        }
      })
      .catch(() => {
        if (token !== requestToken.current) return;
        setNotice({ kind: 'error', code: null });
        setStage('connect');
      });
  };

  const runImport = () => {
    if (selectedMapId === null || stage !== 'pick') return;
    const token = ++requestToken.current;
    setNotice({ kind: 'none' });
    setStage('importing');
    onImport(selectedMapId)
      .then((result) => {
        if (token !== requestToken.current) return;
        if (result.status === 'created' || result.status === 'refreshed') {
          setStage(result.status);
        } else if (result.status === 'not-connected') {
          // The stored token expired between listing and importing; the server has
          // already cleared it, so the honest next step is the sign-in form.
          setClubs([]);
          setNotice({ kind: 'none' });
          setStage('connect');
        } else if (result.status === 'not-found') {
          setNotice({ kind: 'not-found' });
          setStage('pick');
        } else {
          setNotice({ kind: 'error', code: result.code });
          setStage('pick');
        }
      })
      .catch(() => {
        if (token !== requestToken.current) return;
        setNotice({ kind: 'error', code: null });
        setStage('pick');
      });
  };

  const isDone = stage === 'created' || stage === 'refreshed';
  const hasMaps = reviiriMapCount(clubs) > 0;

  return (
    <HudCard
      side="right"
      sideOffset={60}
      onToggleCollapse={onClose}
      collapseIcon="x"
      collapsedTag={t('border.tag')}
      collapsedTitle={t('reviiri.title')}
    >
      <HudHeader
        title={t('reviiri.title')}
        subtitle={stage === 'connect' ? t('reviiri.connectSubtitle') : undefined}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {(stage === 'loading' || stage === 'connecting' || stage === 'importing') && (
          <div style={{ fontSize: 13, color: COLORS.outline }}>
            {stage === 'importing' ? t('reviiri.importing') : t('reviiri.loading')}
          </div>
        )}

        {(stage === 'connect' || stage === 'connecting') && (
          <ConnectForm
            email={email}
            password={password}
            disabled={stage === 'connecting'}
            onEmail={setEmail}
            onPassword={setPassword}
            onSubmit={connect}
          />
        )}

        {stage === 'pick' && !hasMaps && (
          <div style={{ fontSize: 13, color: COLORS.outline }}>{t('reviiri.noMaps')}</div>
        )}

        {stage === 'pick' && hasMaps && (
          <MapPicker clubs={clubs} selectedMapId={selectedMapId} onSelect={setSelectedMapId} />
        )}

        {stage === 'load-error' && (
          <div style={{ fontSize: 13, color: COLORS.error }}>{t('reviiri.loadError')}</div>
        )}

        {isDone && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              {stage === 'created' ? t('reviiri.createdTitle') : t('reviiri.refreshedTitle')}
            </div>
            <div style={{ fontSize: 13, color: COLORS.outline }}>
              {stage === 'created' ? t('reviiri.createdBody') : t('reviiri.refreshedBody')}
            </div>
          </>
        )}

        <NoticeText notice={notice} codeOnly={stage === 'load-error'} />
      </div>

      <div
        style={{
          padding: 12,
          borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          background: COLORS.surface,
          display: 'flex',
          gap: 8,
        }}
      >
        {isDone ? (
          <button className="wh-btn wh-btn-primary" onClick={onClose} style={{ width: '100%' }}>
            {t('reviiri.close')}
          </button>
        ) : (
          <>
            <button
              className="wh-btn wh-btn-outline"
              onClick={onClose}
              style={{ flex: 1 }}
              disabled={stage === 'connecting' || stage === 'importing'}
            >
              {t('reviiri.cancel')}
            </button>

            {stage === 'load-error' ? (
              <button className="wh-btn wh-btn-primary" onClick={() => void loadClubs()} style={{ flex: 1 }}>
                {t('reviiri.retry')}
              </button>
            ) : stage === 'connect' || stage === 'connecting' ? (
              <button
                className="wh-btn wh-btn-primary"
                onClick={connect}
                disabled={!canConnect}
                style={{ flex: 1 }}
              >
                {stage === 'connecting' ? t('reviiri.connecting') : t('reviiri.connect')}
              </button>
            ) : hasMaps ? (
              <button
                className="wh-btn wh-btn-primary"
                onClick={runImport}
                disabled={selectedMapId === null || stage !== 'pick'}
                style={{ flex: 1 }}
              >
                {stage === 'importing' ? t('reviiri.submitting') : t('reviiri.submit')}
              </button>
            ) : null}
          </>
        )}
      </div>
    </HudCard>
  );
}

function ConnectForm({
  email,
  password,
  disabled,
  onEmail,
  onPassword,
  onSubmit,
}: {
  email: string;
  password: string;
  disabled: boolean;
  onEmail: (value: string) => void;
  onPassword: (value: string) => void;
  onSubmit: () => void;
}) {
  const { t } = useTranslation('map');
  const field = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.surfaceContainerHighest}`,
    background: COLORS.surface,
    color: COLORS.onSurface,
    fontSize: 15,
    boxSizing: 'border-box' as const,
  };
  const label = {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.outline,
    margin: '2px 4px 8px',
  };

  return (
    <>
      <label htmlFor="reviiri-email" style={label}>{t('reviiri.emailLabel')}</label>
      <input
        id="reviiri-email"
        type="email"
        value={email}
        onChange={(e) => onEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
        disabled={disabled}
        autoFocus
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        style={field}
      />

      <label htmlFor="reviiri-password" style={{ ...label, marginTop: 12 }}>
        {t('reviiri.passwordLabel')}
      </label>
      <input
        id="reviiri-password"
        type="password"
        value={password}
        onChange={(e) => onPassword(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(); }}
        disabled={disabled}
        style={field}
      />

      <div style={{ fontSize: 11, color: COLORS.outline, margin: '8px 4px 0' }}>
        {t('reviiri.credentialsHint')}
      </div>
    </>
  );
}

function MapPicker({
  clubs,
  selectedMapId,
  onSelect,
}: {
  clubs: ReviiriClub[];
  selectedMapId: string | null;
  onSelect: (mapId: string) => void;
}) {
  return (
    <div role="radiogroup">
      {clubs.map((club) => (
        <div key={club.id} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.outline, margin: '0 4px 6px' }}>
            {club.name}
          </div>
          {club.maps.map((map) => (
            <label
              key={map.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 8,
                cursor: 'pointer',
                background:
                  selectedMapId === map.id ? COLORS.surfaceContainerHighest : 'transparent',
              }}
            >
              <input
                type="radio"
                name="reviiri-map"
                value={map.id}
                checked={selectedMapId === map.id}
                onChange={() => onSelect(map.id)}
                aria-label={map.name}
              />
              <span style={{ fontSize: 13 }}>{map.name}</span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

function NoticeText({ notice, codeOnly = false }: { notice: Notice; codeOnly?: boolean }) {
  const { t } = useTranslation('map');
  if (notice.kind === 'none') return null;
  if (codeOnly && (notice.kind !== 'error' || !notice.code)) return null;

  const body =
    notice.kind === 'rejected' ? t('reviiri.rejected')
    : notice.kind === 'not-found' ? t('reviiri.notFound')
    : t('reviiri.error');

  return (
    <div style={{ fontSize: 13, color: COLORS.error, margin: '12px 4px 0' }}>
      {!codeOnly && body}
      {notice.kind === 'error' && notice.code && (
        <div style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
          {t('reviiri.errorCode', { code: notice.code })}
        </div>
      )}
    </div>
  );
}
